import moment from 'moment';

import { EmploymentPositionProposalProps } from '~/data/proposals';
import { scheduledDateFormats } from '~/features/ScheduledEmploymentChanges/helpers';
import {
  CCCustomValue,
  ScheduleEmploymentFormCostCenterDetails,
  ScheduleEmploymentFormUnitPositionDetails,
  ScheduleEmploymentFormValues,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/useScheduleEmployment';
import {
  EmployeeProposalDecision,
  mapSubmitDataUtils,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { isRoleHR } from '~/services/Permissions';

type CCPostData = {
  fEmpCostCenter: number;
  fEmpCostCenterIsDefault?: boolean;
  fEmpCostCenterPercent?: number;
  fEmpCostCenterCustomData?: Object;
};

type UPPostData = {
  fEmpUnitPos: number;
  fEmpUnitPosPercent: number;
};

export type EmpPositionPostData = {
  fEmpCostCenters: CCPostData[];
  fEmpJobTitle: string;
  fEmpLegalEntity: string;
  fEmpUnitPositions: UPPostData[];
  fInternalPositionId?: number;
  fInternalPositionInfo: string;
  fInternalPositionIsCurrent: boolean;
  fInternalPositionIsPending: boolean;
  fInternalPositionManager: string;
  fInternalPositionPosition: string;
  fInternalPositionStartDate: string;
  fInternalPositionUnit: string;
  fEmpBusinessUnit1: string;
  fInternalPositionEmpId: string;
};

export type EmpPositionProposalPostData = {
  fInternalPosProposalEmplId: number;
  fInternalPosProposalCostCenters: CCPostData[];
  fInternalPosProposalJobTitle: string;
  fInternalPosProposalEntity: string;
  fInternalPosProposalUnitPositions: UPPostData[];
  fInternalPositionId?: number;
  fInternalPosProposalInfo: string;
  fInternalPosProposalMGR: string;
  fInternalPosProposalPosition: string;
  fInternalPosProposalStartDate: string;
  fInternalPosProposalUnit: string;
  fEmpBusinessUnit1: string;
  fInternalPosProposalEmpId: string;
  fInternalPosProposalManagerId: number;
  fInternalPosProposalHigherMgrId: number;
  fInternalPosProposalHrId: number;
  fInternalPosProposalStatus: string;
  fInternalPosProposalSubUid?: string;
};

const mapEmpPositionUnitPositionsData = (
  unitPositionDetails: ScheduleEmploymentFormUnitPositionDetails[],
): UPPostData[] => {
  return unitPositionDetails.map((up): UPPostData => {
    const { unitPosition, unitPositionPercent } = up;

    return {
      fEmpUnitPos: unitPosition,
      fEmpUnitPosPercent: unitPositionPercent || 0,
    };
  });
};

const mapEmpPositionCostCenterCustomData = (
  costCenterDetails: ScheduleEmploymentFormCostCenterDetails[],
): CCPostData[] => {
  return costCenterDetails.map((cc): CCPostData => {
    const { costCenterId, isDefault, percent, customData } = cc;

    const fEmpCostCenterCustomData: CCCustomValue | undefined =
      !!customData && Object.keys(customData).length
        ? removeUndefinedAndNullValuesFromCCCustomValues(customData)
        : undefined;

    return {
      fEmpCostCenter: costCenterId,
      fEmpCostCenterIsDefault: isDefault,
      fEmpCostCenterPercent: percent || 0,
      fEmpCostCenterCustomData,
    };
  });
};

export const mapEmpPositionPostData = (
  data: ScheduleEmploymentFormValues,
  empId: string,
  current: boolean,
  fInternalPositionId?: number,
): EmpPositionPostData => {
  return {
    fEmpCostCenters: mapEmpPositionCostCenterCustomData(data.costCenterDetails),
    fEmpJobTitle: data.fEmpJobTitle,
    fEmpLegalEntity: data.fEmpLegalEntity,
    fEmpUnitPositions: mapEmpPositionUnitPositionsData(
      data.unitPositionDetails,
    ),
    ...(fInternalPositionId && { fInternalPositionId: fInternalPositionId }),
    fInternalPositionInfo: data.fInternalPositionInfo,
    fInternalPositionEmpId: empId,
    fInternalPositionIsCurrent: current,
    fInternalPositionIsPending: true,
    fInternalPositionManager: data.fInternalPositionManager,
    fInternalPositionPosition: data.fInternalPositionPosition,
    fInternalPositionStartDate: moment(data.fInternalPositionStartDate).format(
      scheduledDateFormats.dashFormat,
    ),
    fInternalPositionUnit: data.fInternalPositionUnit.toString(),
    fEmpBusinessUnit1: data.fInternalPositionUnit.toString(),
  };
};

const removeUndefinedAndNullValuesFromCCCustomValues = (
  CC: CCCustomValue,
): CCCustomValue => {
  return Object.fromEntries(
    Object.entries(CC).filter(
      (_, value) => value !== undefined && value !== null,
    ),
  );
};

export const mapEmpPositionProposalPostData = (
  data: ScheduleEmploymentFormValues,
  empId: string,
  loggedInUserRoles: string[],
  emplId: number,
): EmpPositionProposalPostData => {
  const {
    submissionResponsibleHR,
    submissionDateInFormat,
    submissionProposalStatus,
  } = mapSubmitDataUtils({
    defaultSubmissionDateFormat: scheduledDateFormats.dashFormat,
  });
  const isHRUser = isRoleHR(loggedInUserRoles);

  return {
    fInternalPosProposalEmplId: emplId,
    fInternalPosProposalCostCenters: mapEmpPositionCostCenterCustomData(
      data.costCenterDetails,
    ),
    fInternalPosProposalJobTitle: data.fEmpJobTitle,
    fInternalPosProposalEntity: data.fEmpLegalEntity,
    fInternalPosProposalUnitPositions: mapEmpPositionUnitPositionsData(
      data.unitPositionDetails,
    ),
    fInternalPosProposalInfo: data.fInternalPositionInfo,
    fInternalPosProposalEmpId: empId,
    fInternalPosProposalMGR: data.fInternalPositionManager,
    fInternalPosProposalPosition: data.fInternalPositionPosition,
    fInternalPosProposalStartDate: submissionDateInFormat(
      data.fInternalPositionStartDate,
    ),
    fInternalPosProposalUnit: data.fInternalPositionUnit.toString(),
    fEmpBusinessUnit1: data.fInternalPositionUnit.toString(),
    fInternalPosProposalManagerId: -1,
    fInternalPosProposalHigherMgrId: -1,
    fInternalPosProposalHrId: submissionResponsibleHR(
      data.fInternalPositionProposalHrId,
    ),
    fInternalPosProposalStatus: submissionProposalStatus({
      isHRUser,
      decision: undefined,
    }),
  };
};

export const mapEmpPositionProposalPutData = (
  proposal: EmploymentPositionProposalProps,
  loggedInUserRoles: string[],
  decision?: EmployeeProposalDecision,
): EmpPositionProposalPostData => {
  const {
    submissionResponsibleHR,
    submissionDateInFormat,
    submissionProposalStatus,
  } = mapSubmitDataUtils({
    defaultSubmissionDateFormat: scheduledDateFormats.dashFormat,
  });
  const isHRUser = isRoleHR(loggedInUserRoles);
  const {
    proposalData: { ip, up, cc },
  } = proposal;
  return {
    fInternalPosProposalSubUid: proposal.proposalId,
    fInternalPosProposalEmplId: ip.employmentFK,
    fInternalPosProposalCostCenters: cc.map((c) => {
      return {
        fEmpCostCenter: c.costCenterFK,
        ...(c.allocationPercent && {
          fEmpCostCenterPercent: c.allocationPercent || 0,
        }),
        fEmpCostCenterIsDefault: c.isDefault,
      };
    }),
    fInternalPosProposalJobTitle: ip.jobTitle || '',
    fInternalPosProposalEntity: ip.legalEntity.toString(),
    fInternalPosProposalUnitPositions: up.map((untiPos) => {
      return {
        fEmpUnitPos: untiPos.unitPositionFK,
        fEmpUnitPosPercent: untiPos.allocationPercent || 0,
      };
    }),
    fInternalPosProposalInfo: ip.info,
    fInternalPosProposalEmpId: ip.employeeFK.toString(),
    fInternalPosProposalMGR: ip.managerFK.toString(),
    fInternalPosProposalPosition: ip.positionFK,
    fInternalPosProposalStartDate: submissionDateInFormat(ip.startDate),
    fInternalPosProposalUnit: ip.unitFK.toString(),
    fEmpBusinessUnit1: ip.unitFK.toString(),
    fInternalPosProposalManagerId: -1,
    fInternalPosProposalHigherMgrId: -1,
    fInternalPosProposalHrId: submissionResponsibleHR(ip.workflowProps.hrFK),
    fInternalPosProposalStatus: submissionProposalStatus({
      isHRUser,
      decision,
    }),
  };
};
